import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router'
import { Alert, Button } from 'react-bootstrap'
import Icon from 'components/ui/icon'
import { format } from 'date-fns'

import Page from 'components/ui/page'
import PageHeader from 'components/ui/pageHeader'
import EmptyState from 'components/ui/emptyState'
import { LineChartPoint } from 'components/charts/lineChart'
import { TransferIndicators } from './transferIndicators'
import { UserRole } from 'features/user/types'

import TransfersSkeleton from './components/transfersSkeleton'
import TransferFilters, {
  TRANSFER_FILTER_MIN_DATE,
} from './components/transferFilters'
import StatusCards from './components/statusCards'
import TransferPanel, { TransferPanelView } from './components/transferPanel'
import TransferMetrics from './components/transferMetrics'
import {
  STATUS_SELECTION_ORDER,
  compareByDataProcedimento,
  matchesConvenioFilter,
} from './transferStatus'
import { downloadTransfersCsv } from './transferCsv'

import * as transferApi from './api.client'
import * as hospitalApi from '../hospitals/api.client'
import * as doctorApi from '../doctors/api.client'
import {
  CONVENIO_FILTER_ALL,
  ConvenioFilter,
  Transfer,
  TransferStatus,
} from './types'
import { HospitalAvailable } from 'features/hospitals/types'
import { Doctor } from 'features/doctors/types'
import { AccessRequestStatus } from '../accessRequest/types'
import {
  getHospitalSynchronizedAt,
  getUserProfile,
  getUsername,
} from '../user/auth'
import { useOptionalHospitalAdmin } from '../adminHospital/hospitalAdminContext'
import ManagedHospitalSelect from '../adminHospital/components/managedHospitalSelect'
import { formatHospitalSynchronizedAt } from '../adminHospital/hospitalDisplay'

const MASK = 'dd-MM-yyyy'

const getDefaultStatusWithTransfers = (
  indicators: TransferIndicators
): TransferStatus =>
  STATUS_SELECTION_ORDER.find(
    (status) => indicators.getTotalByStatus(status) > 0
  ) ?? TransferStatus.PAGO

const Transfers: FunctionComponent = () => {
  const navigate = useNavigate()
  const hospitalAdminContext = useOptionalHospitalAdmin()
  const adminHospitalId =
    hospitalAdminContext?.selectedHospital?.hospitalId ?? null

  const [transfers, setTransfers] = useState<Transfer[]>([])
  const [chartPoints, setChartPoints] = useState<LineChartPoint[]>([])
  const [transferStatusSelected, setTransferStatusSelected] = useState(
    TransferStatus.PAGO
  )
  const [transferConvenioSelected, setTransferConvenioSelected] =
    useState<ConvenioFilter>(CONVENIO_FILTER_ALL)
  const [indicators, setIndicators] = useState<TransferIndicators>(
    new TransferIndicators()
  )
  const [transfersLoaded, setTransfersLoaded] = useState(false)
  const [hospitals, setHospitals] = useState<HospitalAvailable[]>([])
  const [doctors, setDoctors] = useState<Doctor[]>([])
  const [selectedHospitalId, setSelectedHospitalId] = useState(0)
  const [selectedDoctorId, setSelectedDoctorId] = useState(0)
  const [filter, setFilter] = useState({ start: new Date(), end: new Date() })
  const [errorMessage, setErrorMessage] = useState({ show: false, message: '' })
  const [panelView, setPanelView] = useState<TransferPanelView>('chart')
  const [atendimento, setAtendimento] = useState<string>()
  const [hospitalSyncLabel, setHospitalSyncLabel] = useState<string | null>(() =>
    getUserProfile() === UserRole.HOSPITAL_ADMIN
      ? formatHospitalSynchronizedAt(getHospitalSynchronizedAt())
      : null
  )
  const isInitialAdminLoadRef = useRef(true)
  const filterRef = useRef(filter)
  filterRef.current = filter

  const setTransfersAndBuildIndicators = useCallback(
    (transfers: Transfer[]) => {
      // TODO: Deixar o filtro de repasses com valores negativos configurável
      const transfersWithoutNegatives = transfers.filter(
        (t) => t.valorRepasse > 0
      )

      const indicators = TransferIndicators.build(
        transfersWithoutNegatives,
        transferConvenioSelected
      )
      const statusToSelect = getDefaultStatusWithTransfers(indicators)

      setTransfers(transfersWithoutNegatives)
      setIndicators(indicators)
      setTransferStatusSelected(statusToSelect)
      buildAndSetTransfersForChart(indicators, statusToSelect)
    },
    [transferConvenioSelected]
  )

  const loadTransfersByHospital = useCallback(
    async (hospitalId: number, startDate: Date, endDate: Date) => {
      setTransfersLoaded(false)

      try {
        const transfers = await transferApi.getTransfersByHospital(
          hospitalId,
          format(startDate, MASK),
          format(endDate, MASK)
        )
        setTransfersAndBuildIndicators(transfers)
      } catch (error) {
        setTransfersLoaded(true)
        const errorMessage = 'Ocorreu um erro ao consultar os repasses médicos.'
        console.error(errorMessage, error)
        setErrorMessage({
          show: true,
          message: errorMessage,
        })
      }
    },
    [setTransfersAndBuildIndicators]
  )

  const loadHospitals = useCallback(async () => {
    const initialFilterDates = getInitialFilterDates()

    const hospitals = await hospitalApi.getAvailableHospitals()

    const hospitalDoctorHasAccess = hospitals.filter(
      (hospital) =>
        AccessRequestStatus[hospital.accessRequestStatus] ===
        AccessRequestStatus.APPROVED.toString()
    )

    const firstHospitalId =
      hospitalDoctorHasAccess.length === 0 ? 0 : hospitalDoctorHasAccess[0].id

    setHospitals(hospitalDoctorHasAccess)
    setSelectedHospitalId(firstHospitalId)
    setFilter({
      start: initialFilterDates[0],
      end: initialFilterDates[1],
    })

    loadTransfersByHospital(
      firstHospitalId,
      initialFilterDates[0],
      initialFilterDates[1]
    )
  }, [loadTransfersByHospital])

  const loadTransfersByDoctor = useCallback(
    async (
      hospitalId: number,
      doctorId: number,
      startDate: Date,
      endDate: Date
    ) => {
      setTransfersLoaded(false)

      try {
        const transfers = await transferApi.getTransfersByDoctor(
          hospitalId,
          doctorId,
          format(startDate, MASK),
          format(endDate, MASK)
        )
        setTransfersAndBuildIndicators(transfers)
      } catch (error) {
        setTransfersLoaded(true)
        const errorMessage = 'Ocorreu um erro ao consultar os repasses médicos.'
        setErrorMessage({
          show: true,
          message: errorMessage,
        })
      }
    },
    [setTransfersAndBuildIndicators]
  )

  const loadTransfersByDoctorAndServiceNumber = useCallback(
    async (hospitalId: number, doctorId: number, serviceNumber: number) => {
      setTransfersLoaded(false)

      try {
        if (atendimento !== undefined) {
          const transfers =
            await transferApi.getTransfersByDoctorAndServiceNumber(
              hospitalId,
              doctorId,
              serviceNumber
            )
          setTransfersAndBuildIndicators(transfers)
        }
      } catch (error) {
        setTransfersLoaded(true)
        const errorMessage = 'Ocorreu um erro ao consultar os repasses médicos.'
        setErrorMessage({
          show: true,
          message: errorMessage,
        })
      }
    },
    [atendimento, setTransfersAndBuildIndicators]
  )

  const getInitialFilterDates = (): [Date, Date] => {
    const now = new Date()
    const yearAgo = new Date()
    yearAgo.setFullYear(yearAgo.getFullYear() - 1)

    const start =
      yearAgo < TRANSFER_FILTER_MIN_DATE ? TRANSFER_FILTER_MIN_DATE : yearAgo

    return [start, now]
  }

  const loadTransfers = (startDate: Date, endDate: Date) => {
    if (selectedHospitalId && !atendimento && loggedUserIsDoctor()) {
      loadTransfersByHospital(selectedHospitalId, startDate, endDate)
    } else if (
      adminHospitalId &&
      selectedDoctorId &&
      !atendimento &&
      !loggedUserIsDoctor()
    ) {
      loadTransfersByDoctor(
        adminHospitalId,
        selectedDoctorId,
        startDate,
        endDate
      )
    } else if (adminHospitalId && selectedDoctorId) {
      loadTransfersByDoctorAndServiceNumber(
        adminHospitalId,
        selectedDoctorId,
        // TODO: ajustar os tipos
        atendimento as unknown as number
      )
    }
  }

  const buildIndicatorsByConvenio = (convenio: ConvenioFilter) => {
    const indicators = TransferIndicators.build(transfers, convenio)
    const statusToSelect = getDefaultStatusWithTransfers(indicators)
    setIndicators(indicators)
    setTransferStatusSelected(statusToSelect)
    buildAndSetTransfersForChart(indicators, statusToSelect)
  }

  const updateTransfersOnChart = (status: TransferStatus) => {
    buildAndSetTransfersForChart(indicators, status)
  }

  const buildAndSetTransfersForChart = (
    indicators: TransferIndicators,
    transferStatus: TransferStatus
  ) => {
    const totalsByMonth =
      indicators.getTotalGroupedByMonthAndYearFrom(transferStatus)

    setChartPoints(
      // Keys are month labels (e.g. "ago/25") despite the Map<number> typing.
      Array.from(totalsByMonth.entries()).map(([label, value]) => ({
        label: String(label),
        value,
      }))
    )
    setTransfersLoaded(true)
    setTransferStatusSelected(transferStatus)
  }

  const handleFilterChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    inputValue?: any
  ) => {
    const { name, value } = event.currentTarget

    setFilter({
      ...filter,
      [name]: inputValue !== undefined ? inputValue : value,
    })
  }

  const loggedUserIsDoctor = (): boolean => getUserProfile() === UserRole.DOCTOR

  const changeConvenio = (convenio: ConvenioFilter) => {
    setTransferConvenioSelected(convenio)
    buildIndicatorsByConvenio(convenio)
  }

  const changeSelectedHospital = (hospitalId: number) => {
    setSelectedHospitalId(hospitalId)
    setAtendimento('')
    loadTransfersByHospital(hospitalId, filter.start, filter.end)
  }

  const changeSelectedDoctor = (selectedDoctorId: number) => {
    setSelectedDoctorId(selectedDoctorId)
    setAtendimento('')

    if (adminHospitalId) {
      loadTransfersByDoctor(
        adminHospitalId,
        selectedDoctorId,
        filter.start,
        filter.end
      )
    }
  }

  const changeSelectedAdminHospital = (hospitalId: number) => {
    const hospital = hospitalAdminContext?.managedHospitals.find(
      (item) => item.hospitalId === hospitalId
    )

    if (hospital && hospitalAdminContext) {
      hospitalAdminContext.selectHospital(hospital)
    }
  }

  const openHospitalPage = () => navigate('/hospitals')

  const downloadAll = () => {
    downloadTransfersCsv(
      [...transfers].sort((t1, t2) => (t1.status > t2.status ? 1 : -1)),
      transferStatusSelected
    )
  }

  const downloadByStatus = () => {
    downloadTransfersCsv(transfersForStatusReport, transferStatusSelected)
  }

  useEffect(() => {
    if (loggedUserIsDoctor()) {
      loadHospitals()
    }
  }, [loadHospitals])

  useEffect(() => {
    if (!loggedUserIsDoctor() && adminHospitalId != null) {
      setHospitalSyncLabel(formatHospitalSynchronizedAt(getHospitalSynchronizedAt()))
    }
  }, [adminHospitalId])

  useEffect(() => {
    if (loggedUserIsDoctor()) {
      return
    }

    if (adminHospitalId == null) {
      setDoctors([])
      setSelectedDoctorId(0)
      setTransfers([])
      setChartPoints([])
      setIndicators(new TransferIndicators())
      setTransfersLoaded(true)
      return
    }

    let cancelled = false

    const reloadTransfersForHospital = async () => {
      setTransfersLoaded(false)
      setAtendimento('')
      setErrorMessage({ show: false, message: '' })

      const [startDate, endDate] = isInitialAdminLoadRef.current
        ? getInitialFilterDates()
        : [filterRef.current.start, filterRef.current.end]

      if (isInitialAdminLoadRef.current) {
        isInitialAdminLoadRef.current = false
        setFilter({ start: startDate, end: endDate })
      }

      try {
        const doctors =
          await doctorApi.getDoctorsRelatedToHospital(adminHospitalId)

        if (cancelled) {
          return
        }

        const firstDoctorId = doctors.length === 0 ? 0 : doctors[0].id

        setDoctors(doctors)
        setSelectedDoctorId(firstDoctorId)

        if (firstDoctorId > 0) {
          await loadTransfersByDoctor(
            adminHospitalId,
            firstDoctorId,
            startDate,
            endDate
          )
        } else {
          setTransfers([])
          setChartPoints([])
          setIndicators(new TransferIndicators())
          setTransfersLoaded(true)
        }
      } catch (error) {
        if (cancelled) {
          return
        }

        setTransfersLoaded(true)
        const message = 'Ocorreu um erro ao consultar os repasses médicos.'
        console.error(message, error)
        setErrorMessage({ show: true, message })
      }
    }

    reloadTransfersForHospital()

    return () => {
      cancelled = true
    }
  }, [adminHospitalId, loadTransfersByDoctor])

  const transfersForStatusReport = transfers
    .filter(
      (transfer) =>
        transfer.status === transferStatusSelected &&
        matchesConvenioFilter(transfer, transferConvenioSelected)
    )
    .sort(compareByDataProcedimento)

  const hasTransferContext = selectedHospitalId > 0 || selectedDoctorId > 0
  const showTransfersSkeleton = !transfersLoaded && hasTransferContext
  const adminHasNoDoctors =
    !loggedUserIsDoctor() &&
    adminHospitalId != null &&
    !hospitalAdminContext?.requiresHospitalSelection &&
    doctors.length === 0 &&
    transfersLoaded
  const adminCanShowFilters =
    !loggedUserIsDoctor() &&
    adminHospitalId != null &&
    !hospitalAdminContext?.requiresHospitalSelection
  const showAdminHospitalSelect =
    adminCanShowFilters &&
    (hospitalAdminContext?.managedHospitals.length ?? 0) > 1
  const showFilterForm =
    selectedHospitalId > 0 ||
    (adminCanShowFilters &&
      (doctors.length > 0 || showAdminHospitalSelect))

  const isDoctor = loggedUserIsDoctor()
  const hasData = transfersLoaded && chartPoints.length > 0

  const showingDetail = !showTransfersSkeleton && hasData && panelView === 'detail'

  return (
    // In the detail view the page fits the screen and the table takes the remaining space.
    <Page fill={showingDetail ? 'desktop' : false}>
      <PageHeader
        title="Repasses"
        subtitle="Acompanhe o status dos seus repasses por hospital e período."
        actions={
          hasData && (
            <Button variant="secondary" onClick={downloadAll}>
              <Icon name="download" />
              Baixar todos os repasses
            </Button>
          )
        }
      />

      {isDoctor && selectedHospitalId === 0 && transfersLoaded && (
        <EmptyState
          icon={<Icon name="hospital" size={20} />}
          title={`Olá, ${getUsername() ?? ''}`}
          description="Você ainda não possui acesso aos repasses de nenhum hospital."
          action={
            <Button variant="primary" onClick={openHospitalPage}>
              Solicitar acesso
            </Button>
          }
        />
      )}

      {!isDoctor && hospitalAdminContext?.requiresHospitalSelection && (
        <EmptyState
          icon={<Icon name="hospital" size={20} />}
          title={`Olá, ${getUsername() ?? ''}`}
          description="Selecione o hospital que deseja administrar para consultar os repasses."
          action={<ManagedHospitalSelect />}
        />
      )}

      {errorMessage.show && <Alert variant="danger">{errorMessage.message}</Alert>}

      {showFilterForm && (
        <TransferFilters
          hospitalSelect={
            isDoctor
              ? {
                  options: hospitals,
                  value: selectedHospitalId,
                  onChange: changeSelectedHospital,
                }
              : undefined
          }
          adminHospitalSelect={
            showAdminHospitalSelect && hospitalAdminContext
              ? {
                  options: hospitalAdminContext.managedHospitals,
                  value: adminHospitalId ?? 0,
                  onChange: changeSelectedAdminHospital,
                }
              : undefined
          }
          doctorSelect={
            !isDoctor && doctors.length > 0
              ? {
                  options: doctors,
                  value: selectedDoctorId,
                  onChange: changeSelectedDoctor,
                }
              : undefined
          }
          dates={filter}
          onDateChange={handleFilterChange}
          atendimento={atendimento}
          onAtendimentoChange={setAtendimento}
          convenio={
            selectedHospitalId > 0
              ? { value: transferConvenioSelected, onChange: changeConvenio }
              : undefined
          }
          loading={!transfersLoaded}
          onSearch={() => loadTransfers(filter.start, filter.end)}
          syncLabel={isDoctor ? null : hospitalSyncLabel}
        />
      )}

      {adminHasNoDoctors && (
        <EmptyState
          icon={<Icon name="doctor" size={20} />}
          title="Nenhum médico cadastrado"
          description="Cadastre os médicos do hospital para consultar os repasses."
          action={
            <Button variant="primary" onClick={() => navigate('/doctors/new')}>
              Adicionar médico
            </Button>
          }
        />
      )}

      {showTransfersSkeleton && <TransfersSkeleton />}

      {!showTransfersSkeleton &&
        transfersLoaded &&
        chartPoints.length === 0 &&
        ((isDoctor && selectedHospitalId !== 0) ||
          (!isDoctor && !adminHasNoDoctors && selectedDoctorId > 0)) && (
          <EmptyState
            icon={<Icon name="search" size={18} />}
            title="Nenhum repasse encontrado"
            description="Tente ajustar o período ou os filtros da busca."
          />
        )}

      {!showTransfersSkeleton && hasData && (
        <>
          <StatusCards
            indicators={indicators}
            selected={transferStatusSelected}
            onSelect={updateTransfersOnChart}
          />
          <TransferPanel
            status={transferStatusSelected}
            chartPoints={chartPoints}
            transfers={transfersForStatusReport}
            onDownload={downloadByStatus}
            view={panelView}
            onViewChange={setPanelView}
          />
          {panelView === 'chart' && <TransferMetrics indicators={indicators} />}
        </>
      )}
    </Page>
  )
}

export default Transfers
